import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  IconButton,
  useDisclosure,
  Container,
  Button,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import NavigationDrawer from "../components/NavigationDrawer";
import { useTournament } from "../contexts/TournamentContext";
import { scoresService } from "../services/scoresService";
import { golfPoolService } from "../services/golfPoolService";

const HOLE_PAR = 4; // All holes are par 4 (72 per 18)

const formatScoreRelativeToPar = (score, holesPlayed) => {
  if (!holesPlayed) return "E";
  const diff = score - HOLE_PAR * holesPlayed;
  if (diff === 0) return "E";
  if (diff > 0) return `+${diff}`;
  return diff.toString();
};

// Pool Leaderboard Component
const PoolLeaderboard = ({ poolEntries, scores, selectedRound, poolMaxTeams }) => {
  // Calculate pool leaderboard
  const poolLeaderboard = golfPoolService.calculatePoolLeaderboard(
    poolEntries,
    scores,
    selectedRound
  );

  // Get tie information
  const tieInfo = golfPoolService.getTieInfo(poolLeaderboard);

  // Format pool score relative to par (only for teams that have played)
  const formatPoolScore = (entry) => {
    if (entry.teamsWithScores === 0 || !entry.totalHolesPlayed) return "--";

    const diff = entry.totalScore - HOLE_PAR * entry.totalHolesPlayed;
    if (diff === 0) return "E";
    if (diff > 0) return `+${diff}`;
    return diff.toString();
  };

  return (
    <Box>
      <Heading size="md" mb={4}>
        Golf Pool Leaderboard
        {selectedRound !== 3 && ` - Round ${selectedRound}`}
      </Heading>

      {/* Prize Information */}
      {tieInfo.hasWinners && (
        <Box
          mb={4}
          p={3}
          bg="green.800"
          borderRadius="md"
          border="2px solid"
          borderColor="green.400"
        >
          <Text fontSize="sm" fontWeight="bold" color="green.200" mb={1}>
            {tieInfo.message}
          </Text>
          {/* <Text fontSize="xs" color="gray.300">
            Prize Pool: ${tieInfo.totalPot} • Winners Share: $
            {tieInfo.winnerPot} • Charity: ${tieInfo.charityPot}
          </Text>
          <Text fontSize="xs" color="yellow.200">
            Each winner receives: ${tieInfo.prizeShare}
            {tieInfo.winnerCount > 1 && ` (split ${tieInfo.winnerCount} ways)`}
          </Text> */}
        </Box>
      )}

      {poolEntries.length === 0 ? (
        <Box bg="gray.700" p={4} borderRadius="md" textAlign="center">
          <Text color="gray.300">
            No pool entries yet. Visit the Golf Pool page to enter!
          </Text>
        </Box>
      ) : (
        <VStack spacing={3} align="stretch">
          {poolLeaderboard.map((entry, index) => {
            const isLeading = entry.isLeader && entry.teamsWithScores > 0;
            const formattedScore = formatPoolScore(entry);
            const status = golfPoolService.getPoolEntryStatus(
              entry,
              selectedRound
            );

            // Position display logic
            let positionText = "--";
            if (entry.teamsWithScores > 0) {
              if (entry.isTied) {
                positionText = `T${entry.position}`;
              } else {
                positionText = `${entry.position}`;
              }
            }

            return (
              <Box
                key={entry.entryId}
                bg={isLeading ? "yellow.700" : "blue.700"}
                p={4}
                borderRadius="md"
                _hover={{ bg: isLeading ? "yellow.600" : "blue.600" }}
                border={isLeading ? "2px solid" : "none"}
                borderColor={isLeading ? "yellow.400" : "transparent"}
              >
                <Flex justify="space-between" align="center">
                  <VStack align="flex-start" spacing={1}>
                    <HStack>
                      <Text fontWeight="bold">{positionText}</Text>
                      <Text fontWeight={isLeading ? "bold" : "normal"}>
                        {entry.participant}
                        {isLeading && " 🏆"}
                      </Text>
                      {entry.isTied && entry.teamsWithScores > 0 && (
                        <Text fontSize="xs" color="orange.300">
                          (TIED)
                        </Text>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="gray.300" noOfLines={2}>
                      Teams: {entry.teams.join(", ")}
                    </Text>
                    {entry.teamsWithScores > 0 && (
                      <HStack
                        spacing={4}
                        fontSize="xs"
                        color="gray.400"
                        wrap="wrap"
                      >
                        {entry.teamScores.map((team, idx) => (
                          <Text key={idx}>
                            {team.teamName.split(" / ")[0]}:{" "}
                            {team.score > 0 ? team.score : "NS"}
                          </Text>
                        ))}
                      </HStack>
                    )}
                    {entry.projectedScore > 0 &&
                      entry.teamsWithScores < entry.teams.length && (
                        <Text fontSize="xs" color="yellow.300">
                          Projected: {entry.projectedScore} (based on{" "}
                          {entry.teamsWithScores} teams)
                        </Text>
                      )}
                  </VStack>
                  <VStack align="flex-end" spacing={1}>
                    <Text fontWeight="bold" fontSize="lg">
                      {entry.totalScore}
                    </Text>
                    {entry.teamsWithScores > 0 && (
                      <Text fontSize="sm" color="gray.300">
                        {formattedScore} ({entry.teamsWithScores}/
                        {entry.teams.length})
                      </Text>
                    )}
                    <Text fontSize="xs">{status}</Text>
                  </VStack>
                </Flex>
              </Box>
            );
          })}
        </VStack>
      )}

      {/* <Box mt={4} p={4} bg="blue.800" borderRadius="md">
        <Text fontSize="sm" color="gray.300" mb={2}>
          💡 Pool scoring: Combined score of your {poolMaxTeams} selected teams.
          Lowest total wins! 50% to winners, 50% to Ovarian Cancer Canada.
        </Text>
        <Text fontSize="xs" color="gray.400">
          🏆 In case of ties, prize money is split equally among all tied
          winners.
        </Text>
      </Box> */}
    </Box>
  );
};

const LandingPage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    teamMappingService,
    label,
    venue,
    round1Course,
    round2Course,
    poolMaxTeams,
    year,
  } = useTournament();
  const [selectedRound, setSelectedRound] = useState(1);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [poolEntries, setPoolEntries] = useState([]);
  const [showPoolLeaderboard, setShowPoolLeaderboard] = useState(false);
  const navigate = useNavigate();

  const courseText =
    selectedRound === 1
      ? round1Course
      : selectedRound === 2
      ? round2Course
      : venue;

  // Load scores and set up real-time listener
  useEffect(() => {
    setLoading(true);

    // Set up real-time listener for all scores (works for both authenticated and guest users)
    const unsubscribeScores = scoresService.onScoresChange(
      (newScores) => {
        setScores(newScores);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading scores:", error);
        setLoading(false);
      }
    );

    // Set up real-time listener for pool entries
    const unsubscribePool = golfPoolService.onPoolEntriesChange(
      (entries) => {
        setPoolEntries(entries);
      },
      (error) => {
        console.error("Error loading pool entries:", error);
      }
    );

    // Cleanup listeners on unmount
    return () => {
      unsubscribeScores();
      unsubscribePool();
    };
  }, [year]);

  // Get team score for a specific round
  const getTeamRoundScore = (teamId, round) => {
    const teamScore = scoresService.calculateTeamTotal(teamId, round, scores);
    return teamScore;
  };

  // Get cumulative score for both rounds
  const getCumulativeScore = (teamId) => {
    return scoresService.calculateCumulativeTotal(teamId, scores);
  };

  // Get the score to display based on selected round
  const getDisplayScore = (teamId) => {
    if (selectedRound === 3) {
      // Cumulative
      const cumulative = getCumulativeScore(teamId);
      return {
        score: cumulative.total,
        holesPlayed: cumulative.holesPlayed,
        rounds: 2,
      };
    } else {
      // Individual round
      const roundScore = getTeamRoundScore(teamId, selectedRound);
      return {
        score: roundScore.total,
        holesPlayed: roundScore.holesPlayed,
        rounds: 1,
      };
    }
  };

  // Get status text (e.g., "F", "Thru 9", etc.)
  const getStatusText = (teamId) => {
    if (selectedRound === 3) {
      const round1 = getTeamRoundScore(teamId, 1);
      const round2 = getTeamRoundScore(teamId, 2);

      if (round2.holesPlayed === 18) return "F";
      if (round2.holesPlayed > 0) return `R2 Thru ${round2.holesPlayed}`;
      if (round1.holesPlayed === 18) return "R1 Complete";
      if (round1.holesPlayed > 0) return `R1 Thru ${round1.holesPlayed}`;
      return "Not Started";
    } else {
      const roundScore = getTeamRoundScore(teamId, selectedRound);
      if (roundScore.holesPlayed === 18) return "F";
      if (roundScore.holesPlayed > 0) return `Thru ${roundScore.holesPlayed}`;
      return "Not Started";
    }
  };

  // Create leaderboard data
  const createLeaderboardData = () => {
    const teams = teamMappingService.getAllTeams();

    return teams.map((team) => {
      const displayScore = getDisplayScore(team.id);
      const statusText = getStatusText(team.id);

      return {
        teamId: team.id,
        teamName: team.name,
        players: team.players,
        score: displayScore.score,
        holesPlayed: displayScore.holesPlayed,
        rounds: displayScore.rounds,
        status: statusText,
        formattedScore:
          displayScore.holesPlayed > 0
            ? formatScoreRelativeToPar(
                displayScore.score,
                displayScore.holesPlayed
              )
            : "E",
      };
    });
  };

  const leaderboardData = createLeaderboardData();

  const sortedLeaderboard = leaderboardData
    .filter((team) => team.holesPlayed > 0)
    .sort((a, b) => {
      if (a.score !== b.score) {
        return a.score - b.score;
      }
      return b.holesPlayed - a.holesPlayed;
    });

  const teamsWithoutScores = leaderboardData
    .filter((team) => team.holesPlayed === 0)
    .sort((a, b) => a.teamName.localeCompare(b.teamName));

  const finalLeaderboard = [...sortedLeaderboard, ...teamsWithoutScores];

  if (loading) {
    return (
      <Box minH="100vh" color="white">
        <Container maxW="container.sm" px={4} py={6}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading leaderboard...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" color="white">
      {/* Header */}
      <Box position="fixed" top={4} left={4} zIndex={2}>
        <IconButton
          icon={<HamburgerIcon />}
          onClick={onOpen}
          variant="outline"
          colorScheme="whiteAlpha"
        />
      </Box>

      {/* Navigation Drawer */}
      <NavigationDrawer isOpen={isOpen} onClose={onClose} />

      {/* Main Content */}
      <Container maxW={{ base: "100%", md: "container.sm" }} px={4} py={6}>
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Text fontSize="sm" color="gray.300">
              {label} Golf Tournament
            </Text>
            <Text fontSize="sm" color="gray.300">
              {courseText}
            </Text>
          </Box>

          {/* Round Selection */}
          <HStack spacing={4} justify="center" wrap="wrap">
            <Button
              variant={
                selectedRound === 1 && !showPoolLeaderboard
                  ? "solid"
                  : "outline"
              }
              colorScheme="green"
              onClick={() => {
                setSelectedRound(1);
                setShowPoolLeaderboard(false);
              }}
              size={{ base: "sm", md: "md" }}
            >
              Round 1
            </Button>
            <Button
              variant={
                selectedRound === 2 && !showPoolLeaderboard
                  ? "solid"
                  : "outline"
              }
              colorScheme="green"
              onClick={() => {
                setSelectedRound(2);
                setShowPoolLeaderboard(false);
              }}
              size={{ base: "sm", md: "md" }}
            >
              Round 2
            </Button>
            <Button
              variant={
                selectedRound === 3 && !showPoolLeaderboard
                  ? "solid"
                  : "outline"
              }
              colorScheme="green"
              onClick={() => {
                setSelectedRound(3);
                setShowPoolLeaderboard(false);
              }}
              size={{ base: "sm", md: "md" }}
            >
              Total
            </Button>
            <Button
              variant={showPoolLeaderboard ? "solid" : "outline"}
              colorScheme="blue"
              onClick={() => {
                setShowPoolLeaderboard(true);
                setSelectedRound(3); // Default to cumulative for pool
              }}
              size={{ base: "sm", md: "md" }}
            >
              Golf Pool
            </Button>
          </HStack>

          {/* Leaderboard */}
          {!showPoolLeaderboard ? (
            <Box>
              <Heading size="md" mb={4}>
                {selectedRound === 3
                  ? "Cumulative Leaderboard"
                  : `Round ${selectedRound} Leaderboard`}
              </Heading>
              <VStack spacing={3} align="stretch">
                {finalLeaderboard.map((entry, index) => (
                  <Box
                    key={entry.teamId}
                    bg="green.700"
                    p={4}
                    borderRadius="md"
                    _hover={{ bg: "green.600" }}
                  >
                    <Flex justify="space-between" align="center">
                      <VStack align="flex-start" spacing={1}>
                        <HStack>
                          <Text fontWeight="bold">
                            {entry.holesPlayed > 0 ? `T${index + 1}` : "T--"}
                          </Text>
                          <Text>{entry.players.join(" / ")}</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.300">
                          {entry.holesPlayed > 0
                            ? `${entry.score} strokes`
                            : selectedRound === 3
                            ? "Total: E"
                            : `Round ${selectedRound}: E`}
                        </Text>
                      </VStack>
                      <VStack align="flex-end" spacing={1}>
                        <Text fontWeight="bold">{entry.formattedScore}</Text>
                        <Text fontSize="sm">{entry.status}</Text>
                      </VStack>
                    </Flex>
                  </Box>
                ))}

                {finalLeaderboard.length === 0 && (
                  <Box bg="gray.700" p={4} borderRadius="md" textAlign="center">
                    <Text color="gray.300">
                      No scores recorded yet. Check back soon!
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          ) : (
            <PoolLeaderboard
              poolEntries={poolEntries}
              scores={scores}
              selectedRound={selectedRound}
              poolMaxTeams={poolMaxTeams}
            />
          )}

          {/* Quick Links */}
          <VStack spacing={4} mt={8}>
            <Button
              colorScheme="green"
              size="lg"
              width="full"
              onClick={() => navigate("/live-scoring")}
            >
              Live Scoring
            </Button>
            <Button
              colorScheme="green"
              size="lg"
              width="full"
              onClick={() => navigate("/players")}
            >
              View Golfers
            </Button>
            <Button
              colorScheme="green"
              size="lg"
              width="full"
              variant="outline"
              onClick={() => navigate("/golf-pool")}
            >
              Golf Pool
            </Button>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default LandingPage;
